"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Calculator, Check, Copy, Linkedin, Plus, Share2, X } from "lucide-react";
import Link from "next/link";
import { useCallback, useMemo, useState } from "react";
import { ModelSelector } from "@/components/models/playground-model-selector";
import { Button } from "@/lib/components/button";
import { Card } from "@/lib/components/card";
import { Input } from "@/lib/components/input";
import { XIcon } from "@/lib/icons/XIcon";
import { models, providers, type ModelDefinition, type ProviderModelMapping } from "@llmgateway/models";
import type { ProviderDefinition } from "@llmgateway/models";

// ─── Derived model list ─────────────────────────────────────────────────────

const now = new Date();
const textModelDefs = (models as unknown as ModelDefinition[]).filter(m => {
  if (m.id === "custom" || m.id === "auto") {
    return false;
  }
  if (m.output?.includes("image")) {
    return false;
  }
  if (m.output?.includes("video")) {
    return false;
  }
  const hasActiveProvider = m.providers.some(p => !p.deactivatedAt || new Date(p.deactivatedAt) > now);
  return hasActiveProvider;
});

// ─── Helpers ────────────────────────────────────────────────────────────────

function getModelById(modelId: string): ModelDefinition | undefined {
  return (models as unknown as ModelDefinition[]).find(m => m.id === modelId);
}
function getProviderName(providerId: string): string {
  const p = (providers as unknown as ProviderDefinition[]).find(p => p.id === providerId);
  return p?.name ?? providerId;
}

/** Get the "official" provider mapping — the one matching the model's family */
function getOfficialProvider(model: ModelDefinition): ProviderModelMapping | undefined {
  return model.providers.find(p => p.providerId === model.family);
}

/** Get the cheapest active provider for a model weighted by actual token usage */
function getCheapestProvider(model: ModelDefinition, inputTokens: number, outputTokens: number): ProviderModelMapping | undefined {
  const activeProviders = model.providers.filter(p => !p.deactivatedAt || new Date(p.deactivatedAt) > now);
  if (activeProviders.length === 0) {
    return undefined;
  }
  function hasPricing(p: ProviderModelMapping): boolean {
    if (inputTokens > 0 && p.inputPrice === undefined) {
      return false;
    }
    if (outputTokens > 0 && p.outputPrice === undefined) {
      return false;
    }
    return true;
  }
  function weightedCost(p: ProviderModelMapping): number {
    if (!hasPricing(p)) {
      return Infinity;
    }
    const inPrice = Number(p.inputPrice ?? "0");
    const outPrice = Number(p.outputPrice ?? "0");
    const inCost = inPrice * inputTokens;
    const outCost = outPrice * outputTokens;
    return inCost + outCost;
  }
  const pricedProviders = activeProviders.filter(hasPricing);
  if (pricedProviders.length === 0) {
    return activeProviders[0];
  }
  return pricedProviders.reduce((cheapest, current) => weightedCost(current) < weightedCost(cheapest) ? current : cheapest);
}
function parseModelFromSelector(selectorValue: string): {
  modelId: string;
  providerId: string;
} | null {
  if (!selectorValue) {
    return null;
  }
  if (selectorValue.includes("/")) {
    const [providerId, modelId] = selectorValue.split("/");
    return {
      modelId,
      providerId
    };
  }
  return {
    modelId: selectorValue,
    providerId: ""
  };
}

// ─── Formatting ─────────────────────────────────────────────────────────────

function formatUsd(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 10_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)}K`;
  }
  if (value >= 1) {
    return `$${value.toFixed(2)}`;
  }
  if (value >= 0.001) {
    return `$${value.toFixed(4)}`;
  }
  return `$${value.toFixed(6)}`;
}
function formatPricePerMillion(pricePerToken: number): string {
  return `$${(pricePerToken * 1e6).toFixed(2)}`;
}
function formatTokenCount(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(0)}K`;
  }
  return tokens.toLocaleString();
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface ModelRow {
  id: string;
  selectorValue: string;
  inputTokens: number;
  outputTokens: number;
}
let rowCounter = 0;
const TOKEN_PRESETS = [{
  label: "Light (10K/1K)",
  input: 10_000,
  output: 1_000
}, {
  label: "Medium (100K/10K)",
  input: 100_000,
  output: 10_000
}, {
  label: "Heavy (1M/100K)",
  input: 1_000_000,
  output: 100_000
}, {
  label: "Intensive (10M/1M)",
  input: 10_000_000,
  output: 1_000_000
}];

// ─── Main Component ─────────────────────────────────────────────────────────

export function TokenCostCalculatorClient() {
  const [rows, setRows] = useState<ModelRow[]>(() => [{
    id: `row-${++rowCounter}`,
    selectorValue: "openai/gpt-4o-mini",
    inputTokens: 1_000_000,
    outputTokens: 100_000
  }]);
  const addRow = useCallback(() => {
    setRows(prev => [...prev, {
      id: `row-${++rowCounter}`,
      selectorValue: "",
      inputTokens: 1_000_000,
      outputTokens: 100_000
    }]);
  }, []);
  const removeRow = useCallback((id: string) => {
    setRows(prev_0 => prev_0.length > 1 ? prev_0.filter(r => r.id !== id) : prev_0);
  }, []);
  const updateRow = useCallback((id_0: string, update: Partial<Omit<ModelRow, "id">>) => {
    setRows(prev_1 => prev_1.map(r_0 => r_0.id === id_0 ? {
      ...r_0,
      ...update
    } : r_0));
  }, []);

  // ─── Calculations ─────────────────────────────────────────────────────────

  const calculations = useMemo(() => {
    let officialTotal = 0;
    let gatewayTotal = 0;
    const rowDetails = rows.map(row => {
      const parsed = parseModelFromSelector(row.selectorValue);
      if (!parsed) {
        return {
          row,
          model: null,
          officialMapping: null,
          cheapestMapping: null,
          officialCost: 0,
          gatewayCost: 0
        };
      }
      const model = getModelById(parsed.modelId);
      if (!model) {
        return {
          row,
          model: null,
          officialMapping: null,
          cheapestMapping: null,
          officialCost: 0,
          gatewayCost: 0
        };
      }
      const officialMapping = getOfficialProvider(model) ?? null;
      const cheapestMapping = getCheapestProvider(model, row.inputTokens, row.outputTokens) ?? null;
      const officialInputPrice = Number(officialMapping?.inputPrice ?? "0");
      const officialOutputPrice = Number(officialMapping?.outputPrice ?? "0");
      const officialInputCost = row.inputTokens * officialInputPrice;
      const officialOutputCost = row.outputTokens * officialOutputPrice;
      const officialCost = officialInputCost + officialOutputCost;
      const cheapestInputPrice = Number(cheapestMapping?.inputPrice ?? "0");
      const cheapestOutputPrice = Number(cheapestMapping?.outputPrice ?? "0");
      const gatewayInputCost = row.inputTokens * cheapestInputPrice;
      const gatewayOutputCost = row.outputTokens * cheapestOutputPrice;
      const gatewayCost = gatewayInputCost + gatewayOutputCost;
      officialTotal += officialCost;
      gatewayTotal += gatewayCost;
      return {
        row,
        model,
        officialMapping,
        cheapestMapping,
        officialCost,
        gatewayCost
      };
    });
    const savings = officialTotal - gatewayTotal;
    const savingsPercent = officialTotal > 0 ? (savings / officialTotal * 100).toFixed(1) : "0";
    return {
      rowDetails,
      officialTotal,
      gatewayTotal,
      savings,
      savingsPercent
    };
  }, [rows]);
  return <div className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
			{/* Decorative backdrop */}
			<div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
				<div className="absolute left-1/2 top-0 h-[420px] w-full max-w-[820px] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
				<div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
			</div>
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
					<div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5">
						<Calculator className="h-3.5 w-3.5 text-blue-500" />
						<span className="text-xs font-medium text-blue-600 dark:text-blue-400">
							Free LLM Token Cost Calculator
						</span>
					</div>
					<h1 className="mb-6 text-4xl font-bold tracking-tight text-balance sm:text-5xl lg:text-6xl">
						Calculate your true LLM token costs
					</h1>
					<p className="text-lg text-muted-foreground text-balance leading-relaxed max-w-2xl mx-auto">
						See exactly what GPT-4o, Claude, Gemini, and 200+ models cost at
						official rates, then how much less you&apos;d pay routing each
						request through LLM Gateway&apos;s cheapest provider.
					</p>
					<div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
						<span className="flex items-center gap-1.5">
							<Check className="h-4 w-4 text-green-500" />
							200+ models
						</span>
						<span className="flex items-center gap-1.5">
							<Check className="h-4 w-4 text-green-500" />
							Zero platform markup
						</span>
						<span className="flex items-center gap-1.5">
							<Check className="h-4 w-4 text-green-500" />
							Free, no signup
						</span>
					</div>
				</div>

				<div className="mx-auto max-w-5xl space-y-6">
					{/* Model rows */}
					{rows.map((row_0, index) => <ModelRowCard key={row_0.id} row={row_0} index={index} canRemove={rows.length > 1} onUpdate={updateRow} onRemove={removeRow} />)}

					{/* Add model button */}
					<button type="button" onClick={addRow} className="w-full flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-border hover:border-blue-500/50 hover:bg-blue-500/5 transition-all text-muted-foreground hover:text-blue-500">
						<Plus className="h-4 w-4" />
						<span className="text-sm font-medium">Add another model</span>
					</button>

					{/* Results */}
					<ResultsPanel calculations={calculations} />

					{/* CTA */}
					<div className="mt-16 space-y-6">
						<Card className="p-6 sm:p-8 border-border bg-gradient-to-br from-muted/50 to-muted/30">
							<div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
								<div className="flex-1 text-center sm:text-left">
									<p className="text-lg font-semibold">
										Processing high volume?
									</p>
									<p className="text-sm text-muted-foreground mt-1">
										Enterprise plans include volume discounts, dedicated
										support, custom SLAs, and extended data retention.
									</p>
								</div>
								<Button size="lg" variant="outline" className="shrink-0 bg-transparent" asChild>
									<Link href="/enterprise#contact">
										Talk to Sales
										<ArrowRight className="ml-2 h-4 w-4" />
									</Link>
								</Button>
							</div>
						</Card>

						<Card className="p-8 sm:p-10 border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-blue-600/5 text-center">
							<h2 className="text-2xl sm:text-3xl font-bold mb-3 text-balance">
								Ready to cut your LLM costs?
							</h2>
							<p className="text-muted-foreground mb-6 text-balance leading-relaxed">
								Start for free with no platform fees. No credit card required.
							</p>
							<div className="flex flex-col sm:flex-row gap-3 justify-center">
								<Button size="lg" asChild>
									<Link href="/signup">
										Get Started Free
										<ArrowRight className="ml-2 h-4 w-4" />
									</Link>
								</Button>
								<Button size="lg" variant="outline" className="bg-transparent" asChild>
									<Link href="/enterprise#contact">Book a Demo</Link>
								</Button>
							</div>
						</Card>
					</div>
				</div>
			</div>
		</div>;
}

// ─── Model Row Card ─────────────────────────────────────────────────────────

function ModelRowCard(t0) {
  const $ = _c(64);
  const {
    row,
    index,
    canRemove,
    onUpdate,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== canRemove || $[1] !== index || $[2] !== onRemove || $[3] !== onUpdate || $[4] !== row.id || $[5] !== row.inputTokens || $[6] !== row.outputTokens || $[7] !== row.selectorValue) {
    const parsed = parseModelFromSelector(row.selectorValue);
    const model = parsed ? getModelById(parsed.modelId) : null;
    const officialMapping = model ? getOfficialProvider(model) : null;
    const cheapestMapping = model ? getCheapestProvider(model, row.inputTokens, row.outputTokens) ?? null : null;
    let t2;
    if ($[9] !== onUpdate || $[10] !== row.id) {
      t2 = preset => {
        onUpdate(row.id, {
          inputTokens: preset.input,
          outputTokens: preset.output
        });
      };
      $[9] = onUpdate;
      $[10] = row.id;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    const handlePreset = t2;
    let t3;
    if ($[12] !== canRemove || $[13] !== onRemove || $[14] !== row.id) {
      t3 = canRemove && <button type="button" onClick={() => onRemove(row.id)} className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 focus-visible:opacity-100 focus-visible:text-red-500 focus-visible:bg-red-500/10 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100" aria-label="Remove model"><X className="h-4 w-4" /></button>;
      $[12] = canRemove;
      $[13] = onRemove;
      $[14] = row.id;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    const t4 = index + 1;
    let t5;
    if ($[16] !== t4) {
      t5 = <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted text-xs font-bold text-muted-foreground">{t4}</div>;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    const t6 = `model-${row.id}`;
    let t7;
    if ($[18] !== t6) {
      t7 = <label htmlFor={t6} className="text-xs font-medium text-muted-foreground mb-1.5 block">Model</label>;
      $[18] = t6;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== onUpdate || $[21] !== row.id) {
      t8 = val => onUpdate(row.id, {
        selectorValue: val
      });
      $[20] = onUpdate;
      $[21] = row.id;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    const t9 = `model-${row.id}`;
    let t10;
    if ($[23] !== row.selectorValue || $[24] !== t8 || $[25] !== t9) {
      t10 = <ModelSelector models={textModelDefs as ModelDefinition[]} providers={providers as unknown as ProviderDefinition[]} value={row.selectorValue} onValueChange={t8} placeholder="Select model..." rootOnly={true} id={t9} />;
      $[23] = row.selectorValue;
      $[24] = t8;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== t10 || $[28] !== t7) {
      t11 = <div>{t7}{t10}</div>;
      $[27] = t10;
      $[28] = t7;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    const t12 = `inputTokens-${row.id}`;
    let t13;
    if ($[30] !== t12) {
      t13 = <label htmlFor={t12} className="text-xs font-medium text-muted-foreground mb-1.5 block">Input tokens</label>;
      $[30] = t12;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    const t14 = `inputTokens-${row.id}`;
    let t15;
    if ($[32] !== onUpdate || $[33] !== row.id) {
      t15 = e => onUpdate(row.id, {
        inputTokens: Math.max(0, Number(e.target.value) || 0)
      });
      $[32] = onUpdate;
      $[33] = row.id;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== row.inputTokens || $[36] !== t14 || $[37] !== t15) {
      t16 = <Input id={t14} type="number" min={0} step={1000} value={row.inputTokens} onChange={t15} className="h-10 font-mono text-sm" />;
      $[35] = row.inputTokens;
      $[36] = t14;
      $[37] = t15;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] !== t13 || $[40] !== t16) {
      t17 = <div>{t13}{t16}</div>;
      $[39] = t13;
      $[40] = t16;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    const t18 = `outputTokens-${row.id}`;
    let t19;
    if ($[42] !== t18) {
      t19 = <label htmlFor={t18} className="text-xs font-medium text-muted-foreground mb-1.5 block">Output tokens</label>;
      $[42] = t18;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    const t20 = `outputTokens-${row.id}`;
    let t21;
    if ($[44] !== onUpdate || $[45] !== row.id) {
      t21 = e_0 => onUpdate(row.id, {
        outputTokens: Math.max(0, Number(e_0.target.value) || 0)
      });
      $[44] = onUpdate;
      $[45] = row.id;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    let t22;
    if ($[47] !== row.outputTokens || $[48] !== t20 || $[49] !== t21) {
      t22 = <Input id={t20} type="number" min={0} step={1000} value={row.outputTokens} onChange={t21} className="h-10 font-mono text-sm" />;
      $[47] = row.outputTokens;
      $[48] = t20;
      $[49] = t21;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    let t23;
    if ($[51] !== t19 || $[52] !== t22) {
      t23 = <div>{t19}{t22}</div>;
      $[51] = t19;
      $[52] = t22;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    let t24;
    if ($[54] !== t11 || $[55] !== t17 || $[56] !== t23) {
      t24 = <div className="grid gap-4 sm:grid-cols-[1fr_1fr_1fr] items-end">{t11}{t17}{t23}</div>;
      $[54] = t11;
      $[55] = t17;
      $[56] = t23;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    let t25;
    if ($[58] !== handlePreset || $[59] !== row.inputTokens || $[60] !== row.outputTokens) {
      t25 = TOKEN_PRESETS.map(preset_0 => <button key={preset_0.label} type="button" onClick={() => handlePreset(preset_0)} className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${row.inputTokens === preset_0.input && row.outputTokens === preset_0.output ? "border-blue-500/50 bg-blue-500/10 text-blue-600 dark:text-blue-400" : "border-border text-muted-foreground hover:border-blue-500/30 hover:text-foreground"}`}>{preset_0.label}</button>);
      $[58] = handlePreset;
      $[59] = row.inputTokens;
      $[60] = row.outputTokens;
      $[61] = t25;
    } else {
      t25 = $[61];
    }
    let t26;
    if ($[62] !== t25) {
      t26 = <div className="mt-3 flex flex-wrap gap-1.5">{t25}</div>;
      $[62] = t25;
      $[63] = t26;
    } else {
      t26 = $[63];
    }
    t1 = <Card className="p-5 sm:p-6 border-border bg-card/50 relative group">{t3}<div className="flex items-center gap-2 mb-4">{t5}<span className="text-sm font-medium text-muted-foreground">{model?.name ?? "Select a model"}</span></div>{t24}{t26}{model && officialMapping && cheapestMapping && <div className="mt-4 pt-4 border-t border-border grid gap-3 sm:grid-cols-2"><div className="flex items-center justify-between sm:justify-start gap-4"><div><p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Official ({getProviderName(officialMapping.providerId)})</p><p className="text-sm font-mono mt-0.5"><span className="text-muted-foreground">{formatPricePerMillion(Number(officialMapping.inputPrice ?? "0"))}/M in</span>{" \xB7 "}<span className="text-muted-foreground">{formatPricePerMillion(Number(officialMapping.outputPrice ?? "0"))}/M out</span></p></div></div><div className="flex items-center justify-between sm:justify-start gap-4"><div><p className="text-[11px] font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">LLM Gateway ({getProviderName(cheapestMapping.providerId)})</p><p className="text-sm font-mono mt-0.5"><span className="text-green-600 dark:text-green-400">{formatPricePerMillion(Number(cheapestMapping.inputPrice ?? "0"))}/M in</span>{" \xB7 "}<span className="text-green-600 dark:text-green-400">{formatPricePerMillion(Number(cheapestMapping.outputPrice ?? "0"))}/M out</span></p></div></div></div>}</Card>;
    $[0] = canRemove;
    $[1] = index;
    $[2] = onRemove;
    $[3] = onUpdate;
    $[4] = row.id;
    $[5] = row.inputTokens;
    $[6] = row.outputTokens;
    $[7] = row.selectorValue;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}

// ─── Results Panel ──────────────────────────────────────────────────────────

function ResultsPanel(t0) {
  const $ = _c(102);
  const {
    calculations
  } = t0;
  const [copied, setCopied] = useState(false);
  const {
    rowDetails,
    officialTotal,
    gatewayTotal,
    savings,
    savingsPercent
  } = calculations;
  const hasModels = rowDetails.some(_temp);
  let t1;
  bb0: {
    if (!hasModels) {
      t1 = "";
      break bb0;
    }
    let parts;
    if ($[0] !== gatewayTotal || $[1] !== officialTotal || $[2] !== rowDetails || $[3] !== savings || $[4] !== savingsPercent) {
      const modelLines = rowDetails.filter(_temp2).map(_temp3);
      let t2;
      if ($[6] !== officialTotal) {
        t2 = formatUsd(officialTotal);
        $[6] = officialTotal;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      let t3;
      if ($[8] !== gatewayTotal) {
        t3 = formatUsd(gatewayTotal);
        $[8] = gatewayTotal;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      parts = ["My LLM cost breakdown:", "", ...modelLines, "", `Total: ${t2} → ${t3}`];
      if (savings > 0) {
        parts.push(`Saving ${savingsPercent}% with @llmgateway`);
      }
      parts.push("", "Calculate yours:");
      $[0] = gatewayTotal;
      $[1] = officialTotal;
      $[2] = rowDetails;
      $[3] = savings;
      $[4] = savingsPercent;
      $[5] = parts;
    } else {
      parts = $[5];
    }
    t1 = parts.join("\n");
  }
  const shareText = t1;
  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/token-cost-calculator` : "https://llm-gateway.agenzo.com/token-cost-calculator";
  let t2;
  if ($[10] !== shareText) {
    t2 = async () => {
      await navigator.clipboard.writeText(`${shareText}\n${pageUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    $[10] = shareText;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleCopy = t2;
  const xShareUrl = `https://x.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareText)}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
  if (!hasModels) {
    let t3;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Card className="p-8 text-center border-border bg-card/50"><p className="text-muted-foreground">Select at least one model above to see your cost comparison.</p></Card>;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    return t3;
  }
  let t3;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-xs font-medium text-muted-foreground mb-1">Official Provider Pricing</p>;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== officialTotal) {
    t4 = formatUsd(officialTotal);
    $[14] = officialTotal;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t4) {
    t5 = <p className="text-2xl font-bold">{t4}</p>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="text-xs text-muted-foreground mt-1">direct from providers</p>;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5) {
    t7 = <Card className="p-5 border-border bg-card/50">{t3}{t5}{t6}</Card>;
    $[19] = t5;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">LLM Gateway Pricing</p>;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== gatewayTotal) {
    t9 = formatUsd(gatewayTotal);
    $[22] = gatewayTotal;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <p className="text-2xl font-bold text-green-600 dark:text-green-400">{t9}</p>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="text-xs text-muted-foreground mt-1">cheapest provider per model, no markup</p>;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10) {
    t12 = <Card className="p-5 border-2 border-green-500/50 bg-green-500/5 shadow-sm shadow-green-500/10">{t8}{t10}{t11}</Card>;
    $[27] = t10;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">You Save</p>;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== savings) {
    t14 = savings > 0 ? formatUsd(savings) : "$0.00";
    $[30] = savings;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{t14}</p>;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = savings > 0 ? `${savingsPercent}% less` : "same price, more features";
  let t17;
  if ($[34] !== t16) {
    t17 = <p className="text-xs text-muted-foreground mt-1">{t16}</p>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t15 || $[37] !== t17) {
    t18 = <Card className="p-5 border-2 border-blue-500/50 bg-blue-500/5 shadow-sm shadow-blue-500/10">{t13}{t15}{t17}</Card>;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t12 || $[40] !== t18 || $[41] !== t7) {
    t19 = <div className="grid gap-3 sm:grid-cols-3">{t7}{t12}{t18}</div>;
    $[39] = t12;
    $[40] = t18;
    $[41] = t7;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-400"><Share2 className="h-4 w-4" /></div>;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const t21 = savings > 0 ? `Share your ${savingsPercent}% savings` : "Share these results";
  let t22;
  if ($[44] !== t21) {
    t22 = <p className="text-sm font-semibold">{t21}</p>;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <p className="text-xs text-muted-foreground">Send this breakdown to your team or post it in one click.</p>;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t22) {
    t24 = <div className="flex items-start gap-3">{t20}<div>{t22}{t23}</div></div>;
    $[47] = t22;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = copied ? "outline" : "default";
  let t26;
  if ($[49] !== copied) {
    t26 = copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />;
    $[49] = copied;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  const t27 = copied ? "Copied!" : "Copy link";
  let t28;
  if ($[51] !== handleCopy || $[52] !== t25 || $[53] !== t26 || $[54] !== t27) {
    t28 = <Button onClick={handleCopy} variant={t25} size="sm" className="gap-2">{t26}{t27}</Button>;
    $[51] = handleCopy;
    $[52] = t25;
    $[53] = t26;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <XIcon className="h-4 w-4" />;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== xShareUrl) {
    t30 = <Button variant="outline" size="sm" className="gap-2" asChild={true}><a href={xShareUrl} target="_blank" rel="noopener noreferrer">{t29}Post on X</a></Button>;
    $[57] = xShareUrl;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Button variant="outline" size="sm" className="gap-2" asChild={true}><a href={linkedinShareUrl} target="_blank" rel="noopener noreferrer"><Linkedin className="h-4 w-4" />LinkedIn</a></Button>;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] !== t28 || $[61] !== t30) {
    t32 = <div className="flex flex-wrap items-center gap-2">{t28}{t30}{t31}</div>;
    $[60] = t28;
    $[61] = t30;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== t24 || $[64] !== t32) {
    t33 = <div className="flex flex-col gap-4 rounded-xl border border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-blue-600/5 p-5 sm:flex-row sm:items-center sm:justify-between">{t24}{t32}</div>;
    $[63] = t24;
    $[64] = t32;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <thead><tr className="border-b border-border bg-muted/50"><th className="text-left py-3 px-4 font-medium text-muted-foreground">Model</th><th className="text-right py-3 px-4 font-medium text-muted-foreground">Tokens</th><th className="text-right py-3 px-4 font-medium text-muted-foreground">Official</th><th className="text-right py-3 px-4 font-medium text-muted-foreground">LLM Gateway</th><th className="text-right py-3 px-4 font-medium text-muted-foreground">Saved</th></tr></thead>;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] !== rowDetails) {
    t35 = rowDetails.filter(_temp4).map(_temp5);
    $[67] = rowDetails;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t35) {
    t36 = <tbody>{t35}</tbody>;
    $[69] = t35;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <td className="py-3 px-4" colSpan={2}>Total</td>;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  if ($[72] !== officialTotal) {
    t38 = formatUsd(officialTotal);
    $[72] = officialTotal;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t38) {
    t39 = <td className="py-3 px-4 text-right font-mono">{t38}</td>;
    $[74] = t38;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] !== gatewayTotal) {
    t40 = formatUsd(gatewayTotal);
    $[76] = gatewayTotal;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== t40) {
    t41 = <td className="py-3 px-4 text-right font-mono text-green-600 dark:text-green-400">{t40}</td>;
    $[78] = t40;
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  let t42;
  if ($[80] !== savings) {
    t42 = savings > 0 ? formatUsd(savings) : "\u2014";
    $[80] = savings;
    $[81] = t42;
  } else {
    t42 = $[81];
  }
  let t43;
  if ($[82] !== t42) {
    t43 = <td className="py-3 px-4 text-right font-mono text-blue-600 dark:text-blue-400">{t42}</td>;
    $[82] = t42;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  let t44;
  if ($[84] !== t39 || $[85] !== t41 || $[86] !== t43) {
    t44 = <tfoot><tr className="bg-muted/30 font-medium">{t37}{t39}{t41}{t43}</tr></tfoot>;
    $[84] = t39;
    $[85] = t41;
    $[86] = t43;
    $[87] = t44;
  } else {
    t44 = $[87];
  }
  let t45;
  if ($[88] !== t36 || $[89] !== t44) {
    t45 = <Card className="border-border overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">{t34}{t36}{t44}</table></div></Card>;
    $[88] = t36;
    $[89] = t44;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== savings || $[92] !== savingsPercent) {
    t46 = savings > 0 && <Card className="border-2 border-green-500/30 bg-gradient-to-br from-green-500/10 to-green-600/5 p-8 text-center"><p className="text-sm text-muted-foreground mb-2">At official rates you're overpaying by</p><p className="text-5xl sm:text-6xl font-bold text-green-600 dark:text-green-400 tracking-tight">{formatUsd(savings)}</p><p className="text-lg text-green-600/80 dark:text-green-400/80 mt-2 font-medium">{savingsPercent}% less with LLM Gateway, same models</p><p className="text-sm text-muted-foreground mt-4">Based on the token volumes entered above</p><Button size="lg" className="mt-6" asChild={true}><Link href="/signup">Start saving for free<ArrowRight className="ml-2 h-4 w-4" /></Link></Button></Card>;
    $[91] = savings;
    $[92] = savingsPercent;
    $[93] = t46;
  } else {
    t46 = $[93];
  }
  let t47;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card/50"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10"><svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg></div><div><p className="text-sm font-medium">Smart Routing</p><p className="text-xs text-muted-foreground mt-0.5">Automatically routes to the cheapest available provider for each model</p></div></div>;
    $[94] = t47;
  } else {
    t47 = $[94];
  }
  let t48;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card/50"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10"><svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg></div><div><p className="text-sm font-medium">No Markup</p><p className="text-xs text-muted-foreground mt-0.5">Zero platform fees — you pay exactly what the provider charges</p></div></div>;
    $[95] = t48;
  } else {
    t48 = $[95];
  }
  let t49;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="grid gap-3 sm:grid-cols-3">{t47}{t48}<div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card/50"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10"><svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg></div><div><p className="text-sm font-medium">Automatic Failover</p><p className="text-xs text-muted-foreground mt-0.5">If one provider is down, requests automatically route to the next cheapest</p></div></div></div>;
    $[96] = t49;
  } else {
    t49 = $[96];
  }
  let t50;
  if ($[97] !== t19 || $[98] !== t33 || $[99] !== t45 || $[100] !== t46) {
    t50 = <div className="space-y-4">{t19}{t33}{t45}{t46}{t49}</div>;
    $[97] = t19;
    $[98] = t33;
    $[99] = t45;
    $[100] = t46;
    $[101] = t50;
  } else {
    t50 = $[101];
  }
  return t50;
}
function _temp5(r_3) {
  const rowSavings = r_3.officialCost - r_3.gatewayCost;
  return <tr key={r_3.row.id} className="border-b border-border last:border-0"><td className="py-3 px-4"><p className="font-medium">{r_3.model.name ?? r_3.model.id}</p><p className="text-xs text-muted-foreground">{r_3.cheapestMapping ? `via ${getProviderName(r_3.cheapestMapping.providerId)}` : ""}</p></td><td className="py-3 px-4 text-right font-mono text-xs text-muted-foreground"><span>{formatTokenCount(r_3.row.inputTokens)} in</span><br /><span>{formatTokenCount(r_3.row.outputTokens)} out</span></td><td className="py-3 px-4 text-right font-mono">{formatUsd(r_3.officialCost)}</td><td className="py-3 px-4 text-right font-mono text-green-600 dark:text-green-400">{formatUsd(r_3.gatewayCost)}</td><td className="py-3 px-4 text-right font-mono">{rowSavings > 0 ? <span className="text-blue-600 dark:text-blue-400">{formatUsd(rowSavings)}</span> : <span className="text-muted-foreground">—</span>}</td></tr>;
}
function _temp4(r_2) {
  return r_2.model;
}
function _temp3(r_1) {
  return `- ${r_1.model.name ?? r_1.model.id}: ${formatUsd(r_1.officialCost)} → ${formatUsd(r_1.gatewayCost)}`;
}
function _temp2(r_0) {
  return r_0.model;
}
function _temp(r) {
  return r.model !== null;
}